/**
 * ครึ่งที่ยังมาไม่ถึงของวงจรใบสั่งผลิต — เขียนไว้ตรง ๆ ไม่ปล่อยให้เดา
 *
 * `docs/API_CONTRACT.md` มีหัวข้อ "สิ่งที่ M5 ตรวจไม่ได้" ซึ่งบอกไว้สามข้อ และ
 * หน้าจอนี้ต้องไม่ทำเป็นว่าไม่มีอยู่:
 *
 * 1. **ไม่มีอะไรพาใบสั่งผลิตเข้า `IN_PROCESS` ได้ในบิลด์นี้** สถานะนั้นเกิดจาก
 *    Scan-In ครั้งแรกที่หน้างาน ซึ่งมากับ M6 — จึง **ไม่มีปุ่มใดในหน้าจอนี้ที่พาไป
 *    ที่นั่น** และจะไม่มีจนกว่าเครื่องสแกนจะใช้งานได้จริง
 * 2. **`complete` ตรวจไม่ได้ว่าปิดงานหน้างานครบหรือยัง** เพราะตาราง
 *    `operation_confirmation` ยังไม่มี — ห้ามอ้างว่าตรวจแล้ว
 * 3. **`close` คำนวณผลต่างต้นทุนจริงไม่ได้** เพราะต้องมีต้นทุนจริงจาก Scan-Out —
 *    หน้าจอนี้จึง **ไม่แสดงตัวเลขผลต่างใด ๆ เลย** ตัวเลขที่หน้าตาเหมือนคำนวณแล้ว
 *    แต่คิดมาเอง จะถูกอ่านเป็นตัวเลขทางบัญชี
 *
 * ปุ่มสามปุ่มด้านล่างถูกเรนเดอร์เป็น **disabled พร้อมเหตุผลที่เป็นความจริง** แทนที่
 * จะผูกกับ endpoint ที่ยังไม่มี (`WO-P1-M5-002`) — ปุ่มที่ยิงไปชน `404` แล้วรายงาน
 * ว่า "เซิร์ฟเวอร์ผิดพลาด" คือคำตอบที่ผู้ใช้ทำอะไรต่อไม่ได้เลย
 *
 * ⚠️ เมื่อ `WO-P1-M5-002` (complete/close/cancel) และ M6 (Scan-In/Scan-Out) ลง
 * ต้องกลับมาแก้ไฟล์นี้พร้อมกับเติม client ที่ `lib/productionOrders/client.ts`
 */

import { StatusBadge } from '../StatusBadge';

interface PendingCommand {
  readonly id: string;
  readonly label: string;
  readonly reason: string;
}

const PENDING_COMMANDS: readonly PendingCommand[] = [
  {
    id: 'complete',
    label: 'ปิดงานผลิต (complete)',
    reason:
      'เปลี่ยน IN_PROCESS → COMPLETED และต้องปฏิเสธถ้ายังมีการยืนยันงานที่ค้างอยู่ — ทั้งสองอย่างยังทำไม่ได้ในบิลด์นี้ เพราะยังไม่มีอะไรพาใบเข้า IN_PROCESS และยังไม่มีข้อมูลการยืนยันงานให้ตรวจ',
  },
  {
    id: 'close',
    label: 'ปิดใบสั่งผลิต (close)',
    reason:
      'เปลี่ยน COMPLETED → CLOSED และเป็นจุดคำนวณผลต่างต้นทุน ซึ่งต้องใช้ต้นทุนจริงจากการสแกนจ่ายของหน้างาน — บิลด์นี้ยังไม่มีต้นทุนจริง จึงไม่มีตัวเลขผลต่างให้แสดงและจะไม่แสดงตัวเลขที่คิดขึ้นเอง',
  },
  {
    id: 'cancel',
    label: 'ยกเลิกใบสั่งผลิต (cancel)',
    reason:
      'ยกเลิกได้จาก DRAFT/RELEASED เท่านั้น และต้องปฏิเสธถ้ามีการยืนยันงานที่ปิดไปแล้วผูกอยู่ (ต้องใช้การกลับรายการแทน) — เงื่อนไขข้อหลังยังตรวจไม่ได้ในบิลด์นี้',
  },
];

export function ProductionOrderPendingWork() {
  return (
    <section
      className="cap-card"
      aria-label="ส่วนของวงจรใบสั่งผลิตที่ยังมาไม่ถึง"
      data-testid="production-order-pending-work"
    >
      <div className="cap-card__head">
        <h2 className="cap-card__title">ครึ่งที่หน้างานจะพามา ยังไม่อยู่ในบิลด์นี้</h2>
        <span className="cap-card__hint">
          เขียนไว้ให้ครบ เพราะหน้าจอที่เงียบเรื่องนี้จะถูกอ่านว่าระบบพัง
        </span>
      </div>

      <div className="cap-card__foot">
        <p data-testid="production-order-in-process-note">
          <strong>ไม่มีคำสั่งใดในหน้าจอนี้ที่พาใบสั่งผลิตเข้าสถานะ “กำลังผลิต” (IN_PROCESS)</strong> —
          สถานะนั้นเกิดขึ้นเองเมื่อมีการสแกนเริ่มงาน (Scan-In) ครั้งแรกของใบนั้นสำเร็จที่หน้างาน
          ซึ่งเป็นงานของเครื่องสแกนและยังมาไม่ถึง การใส่ปุ่มไว้ก่อนคือการเปิดทางให้มีคนเปลี่ยนสถานะ
          โดยไม่มีใครสแกนจริง
        </p>
        <p data-testid="production-order-variance-note">
          <strong>ผลต่างต้นทุนของใบนี้ยังคำนวณไม่ได้ และหน้าจอนี้ไม่แสดงตัวเลขใด ๆ แทน</strong> —
          มันถูกคำนวณตอนปิดใบ จากต้นทุนจริงที่เกิดจากการสแกนจ่ายของและปิดงานหน้างาน ช่องว่างที่เห็น
          จึงไม่ใช่ศูนย์ และไม่ใช่ข้อมูลที่โหลดไม่ขึ้น
        </p>

        {/*
          เหตุผลอยู่ติดกับปุ่มที่ถูกปิด ไม่ใช่ tooltip ที่ต้องเอาเมาส์ไปจ่อ — ปุ่มที่
          กดไม่ได้โดยไม่บอกว่าทำไม อ่านเหมือนหน้าจอที่พัง
        */}
        <div className="cap-meta cap-meta--flush">
          {PENDING_COMMANDS.map((command) => (
            <div className="cap-meta__item" key={command.id}>
              <div className="cap-meta__label">
                <button
                  type="button"
                  className="cap-button cap-button--ghost"
                  data-testid={`production-order-pending-${command.id}`}
                  disabled
                >
                  {command.label}
                </button>
              </div>
              <div className="cap-meta__value">{command.reason}</div>
            </div>
          ))}
        </div>

        <p className="cap-notice__note">
          <StatusBadge tone="planned">ยังไม่เปิดใช้งาน</StatusBadge> สามคำสั่งนี้ยังไม่มีปลายทางที่
          เรียกได้จริง หน้าจอจึงไม่ยิงคำขอไปหามัน — ปุ่มที่กดแล้วได้ข้อความว่าไม่พบปลายทาง
          สอนให้คนเลิกอ่านข้อความผิดพลาดไปทั้งหมด
        </p>
      </div>
    </section>
  );
}
